// Rows 4.5 + 4.6 + 4.7 — the shell's global condition banners, rendered by AppShell inside the
// content column (app-wide by construction: AppShell wraps every scoped route). Three banners,
// three independent signals:
//   • Offline (4.5): warning tone, dismissible PER EPISODE — dismissal resets when the browser
//     comes back online, so the next offline episode shows the banner again.
//   • Maintenance (4.6): danger tone, NOT dismissible while the flag is on (a lockdown the user
//     can hide is not a lockdown). Announces assertively: DS Banner derives role="alert" from
//     tone="danger" (Wave 1 severity fix — this superseded SHELL-02's earlier polite-only limit);
//     offline (warning) stays a polite role="status".
//   • Global crash (4.7, CFR-SHELL01-11, from SHELL-02/main): danger tone, dismissible (unlike
//     maintenance — this is a visibility signal about a past event, not an ongoing lockdown).
//     window.onerror/unhandledrejection only; does not replace SubAppBoundary's per-app fallback.
import { useEffect, useState } from "react";
import { WifiOff, Wrench, AlertTriangle } from "lucide-react";
import { Banner } from "@perlab/ui";
import { useOnlineStatus } from "../state/useOnlineStatus";
import { useMaintenanceMode } from "../state/useMaintenanceMode";
import { useGlobalCrash } from "../state/useGlobalCrash";

export function ShellBanners(): React.ReactElement | null {
  const online = useOnlineStatus();
  const maintenance = useMaintenanceMode();
  const { crash, dismiss: dismissCrash } = useGlobalCrash();
  const [offlineDismissed, setOfflineDismissed] = useState(false);

  // Per-episode dismissal keyed on the offline EVENT itself: the browser fires "offline" only
  // on the online→offline transition, so each event IS a new episode. (Keying on derived
  // `online` state misses a rapid online→offline flip batched into one React commit.)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const newEpisode = () => setOfflineDismissed(false);
    window.addEventListener("offline", newEpisode);
    return () => window.removeEventListener("offline", newEpisode);
  }, []);

  const showOffline = !online && !offlineDismissed;
  if (!showOffline && !maintenance && !crash) return null;

  return (
    <div data-app="shell-banners">
      {maintenance && (
        <Banner tone="danger" icon={<Wrench size={16} aria-hidden />}>
          The workspace is in maintenance mode — apps are temporarily unavailable until
          maintenance ends.
        </Banner>
      )}
      {showOffline && (
        <Banner
          tone="warning"
          icon={<WifiOff size={16} aria-hidden />}
          onDismiss={() => setOfflineDismissed(true)}
        >
          You&apos;re offline — data shown may be out of date and changes can&apos;t be saved
          until the connection returns.
        </Banner>
      )}
      {crash && (
        <Banner tone="danger" icon={<AlertTriangle size={16} aria-hidden />} onDismiss={dismissCrash}>
          Something went wrong in the background ({crash.message}) — you may want to reload this
          page.
        </Banner>
      )}
    </div>
  );
}
